import { observable, action, runInAction } from "mobx";
import cloneDeep from "lodash/cloneDeep";
import { ApiService, ApiUrl } from "helpers";
import {
  EMPTY_LAYERS,
  LAYER_NAMES,
  isEmptyLayers,
  resolveValue,
  sourceOf,
} from "helpers/branding/brandingSchema";

const emptyLayers = () => cloneDeep(EMPTY_LAYERS);

// The server returns the settings as a JSON string, or null when the company has no branding.
export const parseLayers = (settingsJson) => {
  try {
    const stored = JSON.parse(settingsJson);
    if (!stored || typeof stored !== "object") return null;
    // Keep only the current layers (older ones are ignored), and fill in any that are missing.
    const layers = emptyLayers();
    LAYER_NAMES.forEach(name => {
      if (stored[name] && typeof stored[name] === "object") layers[name] = stored[name];
    });
    return layers;
  } catch (e) {
    return null;
  }
};

export const BRANDING_SAVE_ERROR = "Branding could not be saved. Please try again.";

const textOf = (value) => (typeof value === "string" && value.trim() ? value.trim() : null);
// AutoWrapper puts the real text in responseException; its top-level message can be generic.
const apiErrorMessage = (data) => textOf(data?.responseException?.exceptionMessage) || textOf(data?.message);

let loadPromise = null;

const brandingStore = observable({
  // Draft being edited on the Branding page: sparse layers of setting overrides.
  // Edited in place, so each field only re-renders when its own key changes.
  branding: emptyLayers(),
  // Last saved layers; null until the company has saved branding. Editors only read this.
  saved: null,
  // Serialized `saved`, so the dirty check only has to serialize the draft.
  savedJson: JSON.stringify(EMPTY_LAYERS),
  // True once the first load has finished, whether or not it worked.
  loaded: false,
  // True when the load failed, so the page does not let a save overwrite branding it never saw.
  loadFailed: false,
  isSaving: false,
  // Message for the last failed save: the API's validation message, or the generic text.
  saveError: null,

  // True when the draft differs from what is saved.
  get isDirty() {
    return JSON.stringify(this.branding) !== this.savedJson;
  },

  /**
   * Loads the company's branding once and returns the same promise to every caller, so editors can
   * wait on it. It never rejects: if the load fails the editors simply use their built-in defaults,
   * and the next call tries again.
   */
  load() {
    if (!loadPromise) {
      loadPromise = ApiService.get(ApiUrl.getCompanyBranding, "", "", false)
        .then(action(response => {
          const layers = parseLayers(response?.data?.result?.settingsJson);
          const saved = layers && !isEmptyLayers(layers) ? layers : null;
          // Edits made before the load finished are kept, applied on top of the loaded settings
          // (a draft built on empty layers must not drop the settings it never saw).
          const draft = this.isDirty ? this.branding : null;
          this.saved = saved;
          this.savedJson = JSON.stringify(saved || EMPTY_LAYERS);
          const base = saved ? cloneDeep(saved) : emptyLayers();
          if (draft) LAYER_NAMES.forEach(name => { base[name] = { ...base[name], ...draft[name] }; });
          this.branding = base;
          this.loadFailed = false;
        }))
        .catch(action(() => {
          this.loadFailed = true;
          loadPromise = null;
        }))
        .then(action(() => { this.loaded = true; }));
    }
    return loadPromise;
  },

  // Effective value of a setting for a scope, from the draft.
  value(scope, key) {
    return resolveValue(this.branding, scope, key);
  },

  source(scope, key) {
    return sourceOf(this.branding, scope, key);
  },

  setValue: action(function (scope, key, value) {
    this.branding[scope][key] = value;
  }),

  clearValue: action(function (scope, key) {
    delete this.branding[scope][key];
  }),

  /**
   * Saves the draft. Saving a draft with no settings removes the company's branding.
   * Resolves true when saved, false when it failed (the draft is kept either way).
   */
  save() {
    const layers = isEmptyLayers(this.branding) ? null : cloneDeep(this.branding);
    const settingsJson = JSON.stringify(layers || EMPTY_LAYERS);
    runInAction(() => { this.isSaving = true; this.saveError = null; });
    return ApiService.edit(ApiUrl.saveCompanyBranding, { settingsJson })
      .then(action(response => {
        if (response?.data?.isError || response?.data?.success === false) throw response.data;
        this.saved = layers;
        this.savedJson = settingsJson;
        return true;
      }))
      .catch(action(error => {
        this.saveError = apiErrorMessage(error) || BRANDING_SAVE_ERROR;
        return false;
      }))
      .then(action(ok => {
        this.isSaving = false;
        return ok;
      }));
  },

  // Throws away unsaved edits (back to the saved settings), e.g. when leaving the page.
  discardDraft: action(function () {
    this.branding = this.saved ? cloneDeep(this.saved) : emptyLayers();
  }),

  // Clears only the settings of one scope.
  resetScope: action(function (scope) {
    this.branding[scope] = {};
  }),
});

export default brandingStore;
